import { openDB, DBSchema, IDBPDatabase } from 'idb'

export interface QueuedMutation {
  id: string
  type: 'create' | 'update' | 'delete'
  resource: string // e.g., 'aircraft', 'journals'
  resourceId?: string // For update/delete operations
  endpoint: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  data?: any
  timestamp: number
  retryCount: number
  lastError?: string
}

interface OfflineQueueDB extends DBSchema {
  mutations: {
    key: string
    value: QueuedMutation
    indexes: {
      timestamp: number
      resource: string
    }
  }
}

class OfflineQueueService {
  private db: IDBPDatabase<OfflineQueueDB> | null = null
  private readonly DB_NAME = 'flight-journal-offline'
  private readonly DB_VERSION = 1
  private readonly MAX_RETRY_COUNT = 3

  /**
   * Initialize the IndexedDB database
   */
  async init(): Promise<void> {
    if (this.db) return

    this.db = await openDB<OfflineQueueDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('mutations')) {
          const store = db.createObjectStore('mutations', { keyPath: 'id' })
          store.createIndex('timestamp', 'timestamp')
          store.createIndex('resource', 'resource')
        }
      },
    })
  }

  /**
   * Add a mutation to the queue
   */
  async addMutation(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>): Promise<string> {
    await this.init()

    const queuedMutation: QueuedMutation = {
      ...mutation,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retryCount: 0,
    }

    await this.db!.add('mutations', queuedMutation)
    console.log('[OfflineQueue] Mutation queued:', queuedMutation)
    return queuedMutation.id
  }

  /**
   * Get all queued mutations, ordered by timestamp
   */
  async getAllMutations(): Promise<QueuedMutation[]> {
    await this.init()
    const mutations = await this.db!.getAllFromIndex('mutations', 'timestamp')
    return mutations
  }

  /**
   * Get mutations for a specific resource
   */
  async getMutationsByResource(resource: string): Promise<QueuedMutation[]> {
    await this.init()
    return this.db!.getAllFromIndex('mutations', 'resource', resource)
  }

  /**
   * Get a single mutation by ID
   */
  async getMutation(id: string): Promise<QueuedMutation | undefined> {
    await this.init()
    return this.db!.get('mutations', id)
  }

  /**
   * Update a mutation (e.g., increment retry count, update error)
   */
  async updateMutation(id: string, updates: Partial<QueuedMutation>): Promise<void> {
    await this.init()
    const mutation = await this.getMutation(id)
    if (mutation) {
      const updated = { ...mutation, ...updates }
      await this.db!.put('mutations', updated)
    }
  }

  /**
   * Remove a mutation from the queue (after successful sync)
   */
  async removeMutation(id: string): Promise<void> {
    await this.init()
    await this.db!.delete('mutations', id)
    console.log('[OfflineQueue] Mutation removed:', id)
  }

  /**
   * Clear all mutations from the queue
   */
  async clearAll(): Promise<void> {
    await this.init()
    await this.db!.clear('mutations')
    console.log('[OfflineQueue] Queue cleared')
  }

  /**
   * Get the count of queued mutations
   */
  async getCount(): Promise<number> {
    await this.init()
    return this.db!.count('mutations')
  }

  /**
   * Check if a mutation should be retried
   */
  shouldRetry(mutation: QueuedMutation): boolean {
    return mutation.retryCount < this.MAX_RETRY_COUNT
  }

  /**
   * Increment retry count for a mutation
   */
  async incrementRetryCount(id: string, error?: string): Promise<void> {
    const mutation = await this.getMutation(id)
    if (mutation) {
      await this.updateMutation(id, {
        retryCount: mutation.retryCount + 1,
        lastError: error,
      })
    }
  }

  /**
   * Get mutations that have exceeded max retries
   */
  async getFailedMutations(): Promise<QueuedMutation[]> {
    const allMutations = await this.getAllMutations()
    return allMutations.filter(m => m.retryCount >= this.MAX_RETRY_COUNT)
  }

  /**
   * Remove mutations that have exceeded max retries
   */
  async clearFailedMutations(): Promise<void> {
    const failed = await this.getFailedMutations()
    for (const mutation of failed) {
      await this.removeMutation(mutation.id)
    }
    console.log(`[OfflineQueue] Cleared ${failed.length} failed mutations`)
  }
}

// Export singleton instance
export const offlineQueue = new OfflineQueueService()
